/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import withStyles from '@material-ui/core/styles/withStyles';
import { withRouter } from 'react-router';
import {
    object, string, func, shape, array, bool, number, arrayOf,
} from 'prop-types';
import { format } from 'date-fns';

// gql & helper functions
import { ducks as checkoutDucks } from '../../../../../../../../state/ducks/Checkout';
import { getContactId } from '../../../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getShopTheSite } from '../../../../../../../pages/Account/utils/shopTheSite';
import { isEmpty } from '../../../../../../../pages/Account/utils/object';
import {
    getIsProductFilterZipcodeValid, getWineData, getLocationType, getUserSubmittedProductFilterZipcode,
} from '../../../../../../../../state/ducks/App/App-Selectors';
import { getFeatureFlags } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const {
    common: {
        commonSelectors: {
            getLoaderStatus,
            getError,
        },
    },
    checkout: { checkoutActions },
} = checkoutDucks;

const styles = ({ palette, typography, breakpoints }) => ({
    buttonWrap: {
        width: '100%',
        boxSizing: 'border-box',
        marginTop: '15px',
        '& .Mui-disabled': {
            opacity: '0.65',
            color: `${palette.common.gray_900} !important`,
            backgroundColor: palette.primary.main,
        },
    },
    addToCartButton: {
        color: palette.white,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '700',
        width: '235px',
        height: '50px',
        fontSize: '1em',
        textTransform: 'uppercase',
        borderRadius: '28px',
        lineHeight: '1.46em',
        padding: '7px 24px 5px 24px',
        '&:disabled': {
            backgroundColor: palette.cms?.wineVerifyDisable,
            color: palette.disableButton,
        },
    },
    addSubscriptionToCartButton: {
        color: palette.white,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '600',
        width: '255px',
        height: '50px',
        fontSize: '1em',
        textTransform: 'uppercase',
        borderRadius: '28px',
        lineHeight: '1.46em',
        '&:disabled': {
            backgroundColor: palette.cms?.wineVerifyDisable,
            color: palette.disableButton,
        },
        // Mobile
        [breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    cyoAddToCartButton: {
        color: palette.white,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '700',
        width: '100%',
        height: '40px',
        fontSize: '1em',
        textTransform: 'uppercase',
        borderRadius: '4px',
        lineHeight: '1.46em',
        padding: '5px 24px 5px 24px',
        [breakpoints.down(959)]: {
            marginRight: '10px',
            fontSize: '14px !important',
            width: '-webkit-fill-available',
        },
    },
    addToCartBg: {
        backgroundColor: palette.primaryButton,
    },
    addToCartButtonFullWidth: {
        color: palette.white,
        backgroundColor: palette.primaryButton,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '700',
        width: '100%',
        height: '50px',
        fontSize: '1em',
        textTransform: 'uppercase',
        borderRadius: '28px',
        lineHeight: '1.46em',
        padding: '7px 24px 5px 24px',
        boxShadow: 'none',
        '&:hover, &:focus': {
            boxShadow: 'none',
            color: palette.common?.gray_900,
            backgroundColor: palette.primaryButtonHover,
        },
        '&:disabled': {
            backgroundColor: palette.cms?.wineVerifyDisable,
            color: palette.primary?.contrastText,
        },
    },
    sorryMessage: {
        fontSize: '15px',
        color: palette.cms?.errorZipVerifyPDP,
        fontFamily: typography?.fontFamily,
        textAlign: 'center',
        margin: '12px 0px 0px 0px',
    },
    outOfStockMessage: {
        fontSize: '15px',
        color: palette.cms?.errorZipVerifyPDP,
        fontFamily: typography?.fontFamily,
        textAlign: 'center',
        margin: 0,
    },
});

const DesktopPlantsAddToCart = ({
    classes,
    history,
    selectedItemId,
    brandId,
    actionAddToCart,
    partNumber,
    fullWidth,
    cyoSelectedComponents,
    disableAddToCart,
    personalizedData,
    isPersonalizable,
    isAddToCartEnabled,
    wineValidation,
    addons,
    closeAddonModal,
    handleMiniCartModalClick,
    contactId,
    quantity,
    error,
    featureFlags,
    isPassportBundleProduct,
    setAddToCartClicked,
    selectedState,
    deliveryDate,
    stateEnabled,
    alertType,
    selectedSku,
    location,
}) => {
    const MINI_CART_ENABLED = featureFlags['is-mini-cart-enabled'];
    const enableMinicart = MINI_CART_ENABLED;
    const locationType = useSelector(getLocationType);
    const userSubmittedZip = useSelector(getUserSubmittedProductFilterZipcode);
    const handleAddToCart = (productId = null, returnObject = false) => {
        setAddToCartClicked(true);
        if ((stateEnabled && !selectedState) || alertType) return null;

        let categoryId = null;
        let categoryName = null;
        let categoryPath = null;
        // if the user direct loads a product page there will be no categoryId

        if (location) {
            categoryPath = location.pathname;

            if (location.state) {
                const { fromCategoryPage } = location.state;

                if (fromCategoryPage) {
                    if (location.state.categoryId) {
                        categoryId = location.state.categoryId;
                    }

                    if (location.state.categoryPath) {
                        categoryPath = location.state.categoryPath;
                    }

                    if (location.state.categoryName) {
                        categoryName = location.state.categoryName;
                    }
                }
            }
        } else {
            categoryId = null;
        }

        // Attributes
        const attributes = [];

        // Attr: categoryId
        if (categoryId) {
            attributes.push({
                attributeName: 'categoryId',
                attributeValue: categoryId,
            });
        }

        // Attr: categoryIdent
        if (categoryName) {
            attributes.push({
                attributeName: 'categoryIdent',
                attributeValue: categoryName,
            });
        }

        // Attr: categoryPath
        if (categoryPath) {
            attributes.push({
                attributeName: 'categoryPath',
                attributeValue: categoryPath,
            });
        }
        const selectedDeliveryDate = deliveryDate ? deliveryDate.data.deliveryDate || format(deliveryDate.date, 'yyyy-MM-dd') : '';
        const delivery = deliveryDate ? {
            calendarDateType: deliveryDate.data.calendarDateType,
            deliveryDate: selectedDeliveryDate,
            zipCode: userSubmittedZip,
            locationType,
            deliverySLA: deliveryDate.data.deliverySLA,
        } : null;
        const reqObj = {
            brandCode: brandId,
            productCode: (productId || selectedItemId),
            quantity: Math.floor(Number(quantity)) || 1,
            delivery,
            attributes,
        };

        // GiftList - get Shop The Site content
        const giftList = getShopTheSite(contactId) || {};
        if (!isEmpty(giftList) && giftList.products) {
            // Get xOrderAttrValues from the first product for 4.2
            const [{ xOrderAttrValues = {} }] = giftList.products;
            delete giftList.products;
            reqObj.giftList = { ...giftList, xOrderAttrValues, contactId };
        }

        if (isPersonalizable && personalizedData?.personalizationType) {
            reqObj.personalization = personalizedData;
        }

        if (addons.length > 0) {
            if (cyoSelectedComponents.length > 0) {
                reqObj.components = [...cyoSelectedComponents, ...addons];
            } else {
                reqObj.components = addons;
            }
        }
        let passportBundle = {};
        if (isPassportBundleProduct?.enable) {
            passportBundle = {
                enable: true,
                passportSku: isPassportBundleProduct?.passportSku,
            };
        }

        if (returnObject) {
            return {
                reqObj,
                history,
                partNumber,
                categoryId,
                categoryName,
                enableMinicart,
                categoryPath,
                handleMiniCartModalClick,
                deliveryDate,
            };
        }
        const retailPriceObj = {
            USD: selectedSku?.prices?.[0]?.value,
        };
        actionAddToCart(
            reqObj,
            history,
            null,
            categoryId,
            partNumber,
            false,
            selectedDeliveryDate,
            categoryName,
            categoryPath,
            false,
            null,
            selectedSku,
            passportBundle,
            retailPriceObj,
            enableMinicart,
            handleMiniCartModalClick,
        );
        return true;
    };
    let conditionalClasses;
    if (fullWidth) {
        conditionalClasses = classes.addToCartButtonFullWidth;
    } else {
        conditionalClasses = classes.addToCartButton;
    }
    const getAvailabilityAddCart = () => {
        let availability = false;
        if (wineValidation) {
            const keys = Object.keys(wineValidation?.wineAvailability);
            keys.forEach((key) => {
                if (wineValidation?.wineAvailability[key].isProductAvailable) {
                    availability = true;
                }
            });
        }
        return availability;
    };
    const isAddToCartDisabled = (disableAddToCart || (!isAddToCartEnabled && !getAvailabilityAddCart()));
    const errorMessage = (
        <>
            {error?.cart && error.cart !== ''
                ? (
                    <div>
                        <p className={classes.sorryMessage}>We&apos;re sorry.</p>
                        <p className={classes.outOfStockMessage}>This item is no longer available</p>
                    </div>
                )
                : null}
        </>
    );

    const layout = (
        <div className={classes.buttonWrap} id="contAddToCartBtn">
            <Button
                id="pwaAddToCartBtn"
                disabled={isAddToCartDisabled}
                onClick={() => {
                    handleAddToCart();
                    if (enableMinicart) {
                        closeAddonModal();
                    }
                }}
                className={conditionalClasses}
                variant="contained"
            >
                Add to cart
            </Button>
        </div>
    );

    return (
        <>
            {layout}
            {errorMessage}
        </>
    );
};

DesktopPlantsAddToCart.propTypes = {
    classes: object.isRequired,
    history: object.isRequired,
    error: shape({
        cart: string.isRequired,
    }),
    partNumber: string.isRequired,
    selectedItemId: string,
    brandId: string.isRequired,
    actionAddToCart: func.isRequired,
    fullWidth: bool,
    cyoSelectedComponents: array,
    disableAddToCart: bool,
    personalizedData: shape({
        personalizationType: string,
        personalizationLines: arrayOf({
            perAttributeValue: string,
        }),
    }),
    isPersonalizable: bool,
    wineValidation: shape({
        wineAvailability: object, // object type array
        wineVerify: shape({
            birthDay: 'string',
            birthMonth: 'string',
            birthYear: 'string',
            minAge: 'string',
        }),
    }),
    isAddToCartEnabled: bool.isRequired,
    addons: arrayOf(shape({
        brandCode: string,
        productCode: number,
        componentType: string,
        productId: string,
        quantity: number,
    })),
    closeAddonModal: func,
    handleMiniCartModalClick: func,
    contactId: string,
    quantity: string,
    featureFlags: object.isRequired,
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    setAddToCartClicked: func,
    selectedState: func,
    deliveryDate: object,
    stateEnabled: bool.isRequired,
    alertType: string,
    selectedSku: object.isRequired,
    location: object.isRequired,
};

DesktopPlantsAddToCart.defaultProps = {
    selectedItemId: null,
    fullWidth: false,
    cyoSelectedComponents: [],
    error: {},
    disableAddToCart: false,
    personalizedData: {},
    isPersonalizable: false,
    addons: [],
    closeAddonModal: () => { },
    handleMiniCartModalClick: () => { },
    contactId: '',
    quantity: '1',
    wineValidation: null,
    isPassportBundleProduct: {},
    setAddToCartClicked: () => { },
    selectedState: () => { },
    alertType: '',
    deliveryDate: {},
};

const mapStateToProps = (state) => ({
    contactId: getContactId(state),
    zipValid: getIsProductFilterZipcodeValid(state),
    wineValidation: getWineData(state),
    showLoadingMessage: getLoaderStatus(state),
    error: getError(state),
    featureFlags: getFeatureFlags(state),
});

const mapDispatchToProps = (dispatch) => ({
    actionAddToCart: bindActionCreators(checkoutActions.addToCart, dispatch),
});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(DesktopPlantsAddToCart)),
);
